/**
 * Exporte für den Steuerberater (spec §1.13). Auf Knopfdruck, nie automatisch: die Auszahlungen eines
 * Monats werden erst am 2.–7. des Folgemonats erfasst, der Monat ist also fertig, wenn Bastian es sagt.
 * Jeder Druck erzeugt ein eigenes Dokument; ältere Archive bleiben lesbar.
 *
 * Die einzige Absage, die der Benutzer selbst auflösen kann, ist `belege_fehlen`: der Server nennt in
 * seiner deutschen Meldung die fehlenden Belege namentlich (services/export.ts), hier steht sie
 * wörtlich, und derselbe Monat lässt sich mit einem Knopf noch einmal starten, sobald die Belegstrecke
 * aufgeholt hat.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactElement, type ReactNode } from 'react'
import type { MonatsExport } from '../../../../shared/schemas/export'
import { monatsExportSchema } from '../../../../shared/schemas/export'
import { api, istApiError, type DokumentZeile } from '../../../api'
import { Feld } from '../../../components/Feld'
import { Knopf } from '../../../components/Knopf'
import { Leiste } from '../../../components/Leiste'
import { Tabelle } from '../../../components/Tabelle'
import { TabellenZustand, fehlermeldung } from '../../../components/Zustand'
import { datumZeit } from '../../../format'

/**
 * Die Monatsnamen der Oberfläche. Der Server hat dieselbe Liste (services/export.ts, `MONTH_NAMES_DE`)
 * für die Beschriftung im ZIP; Worker-Code wird im Browser nicht geladen, deshalb steht sie hier ein
 * zweites Mal — als reiner Anzeigetext, aus dem nichts gerechnet wird.
 */
export const MONATSNAMEN: readonly string[] = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

/** So viele <th> stehen im Kopf der Tabelle — die Zustandszeilen darunter spannen genau darüber. */
const SPALTEN = 4

const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

/** Dateigrößen sind kein Geldbetrag und kein Datum, aber deutsch formatiert gehören sie trotzdem. */
export function dateigroesse(bytes: number): string {
  if (bytes < 1024) return `${ZAHL.format(bytes)} B`
  if (bytes < 1024 * 1024) return `${ZAHL.format(bytes / 1024)} kB`
  return `${ZAHL.format(bytes / (1024 * 1024))} MB`
}

/** '2026-08' → 'August 2026'; ein unerwarteter Wert bleibt unverändert stehen. */
export function zeitraumText(period: string | null): string {
  if (period === null || !/^\d{4}-\d{2}$/.test(period)) return period ?? '—'
  const name = MONATSNAMEN[Number(period.slice(5, 7)) - 1]
  return name === undefined ? period : `${name} ${period.slice(0, 4)}`
}

interface MonatsWahl { jahr: number; monat: number }

/** Der Vormonat des Berliner Heute — der Monat, den man am ersten Werktag danach ausgeben will. */
export function letzterMonat(heute: Date): MonatsWahl {
  const monat = heute.getMonth() // 0-basiert, also bereits der Vormonat als 1-basierte Zahl
  return monat === 0 ? { jahr: heute.getFullYear() - 1, monat: 12 } : { jahr: heute.getFullYear(), monat }
}

/**
 * Beide Felder stehen als Text im Zustand, nicht als Zahl: eine geleerte Zahleneingabe wäre sonst
 * sofort eine 0 im Feld, und der Benutzer tippte gegen sie an. Aus Text wird eine Zahl erst beim
 * Absenden — durch dasselbe Schema, mit dem der Server den Körper prüft.
 */
interface Eingabe { jahr: string; monat: string }

interface Eingabefehler { feld: string | null; meldung: string }

interface ZodMeldung { path: readonly PropertyKey[]; message: string }

function ersterFehler(fehler: { issues: readonly ZodMeldung[] }): Eingabefehler {
  const erste = fehler.issues[0]
  if (erste === undefined) return { feld: null, meldung: 'Die Eingabe ist ungültig.' }
  const feld = erste.path[0]
  return { feld: typeof feld === 'string' ? feld : null, meldung: erste.message }
}

/** Die Meldung für genau dieses Feld — oder `undefined`, damit `Feld` es nicht als ungültig markiert. */
function meldungFuer(fehler: Eingabefehler | null, feld: string): string | undefined {
  return fehler !== null && fehler.feld === feld ? fehler.meldung : undefined
}

/**
 * Der Bildschirm bekommt nur den Auftraggeber: er hat keine Liste zum Filtern und keinen Datensatz zum
 * Aufklappen, also steht auch nichts von ihm in den Suchparametern. Der gewählte Monat ist eine
 * Eingabe, kein Filter — er wählt nichts aus, was auf dem Bildschirm zu sehen wäre.
 */
export interface ExporteSeitenProps { tenantId: string }

export function ExporteSeite({ tenantId }: ExporteSeitenProps): ReactElement {
  // Ein Pfadabschnitt aus einem Route-Parameter geht nirgends ungeprüft in eine Adresse.
  const tid = encodeURIComponent(tenantId)
  const queryClient = useQueryClient()
  const [eingabe, setzeEingabe] = useState<Eingabe>(() => {
    const vormonat = letzterMonat(new Date())
    return { jahr: String(vormonat.jahr), monat: String(vormonat.monat) }
  })
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)
  const [zuletzt, setzeZuletzt] = useState<DokumentZeile | null>(null)

  const exporte = useQuery({
    queryKey: ['exporte', tenantId],
    queryFn: () => api.get<{ exporte: DokumentZeile[] }>(`/api/t/${tid}/exporte`),
  })

  const bauen = useMutation({
    mutationFn: (monat: MonatsExport) =>
      api.post<{ dokument: DokumentZeile }>(`/api/t/${tid}/exporte/monat`, monat),
    onSuccess: (antwort) => {
      setzeZuletzt(antwort.dokument)
      void queryClient.invalidateQueries({ queryKey: ['exporte', tenantId] })
    },
  })

  // Auch der zweite Versuch geht durch genau diesen Weg: er liest denselben Zustand und schickt
  // deshalb denselben Monat, statt sich einen zu merken, der inzwischen woanders steht.
  const starten = (): void => {
    const geprueft = monatsExportSchema.safeParse({
      jahr: Number(eingabe.jahr),
      monat: Number(eingabe.monat),
    })
    if (!geprueft.success) {
      bauen.reset()
      setzePruefFehler(ersterFehler(geprueft.error))
      return
    }
    setzePruefFehler(null)
    bauen.mutate(geprueft.data)
  }

  const belegeFehlen = istApiError(bauen.error) && bauen.error.error === 'belege_fehlen'

  return (
    <div className="seite">
      <h1>Exporte</h1>
      <p className="hinweis">
        Ein Monat wird ausgegeben, wenn er vollständig ist — Rechnungen, Stornorechnungen und
        Auszahlungsbelege des Monats liegen im ZIP neben den beiden CSV-Dateien und der Monatsübersicht.
      </p>

      <Leiste beschriftung="Monat erstellen" bereich>
        <Feld label="Jahr" htmlFor="ex-jahr" fehler={meldungFuer(pruefFehler, 'jahr')}>
          <input
            id="ex-jahr"
            type="text"
            inputMode="numeric"
            className="mono"
            size={6}
            value={eingabe.jahr}
            onChange={(e) => { setzeEingabe({ ...eingabe, jahr: e.target.value }) }}
          />
        </Feld>
        <Feld label="Monat" htmlFor="ex-monat" fehler={meldungFuer(pruefFehler, 'monat')}>
          <select
            id="ex-monat"
            value={eingabe.monat}
            onChange={(e) => { setzeEingabe({ ...eingabe, monat: e.target.value }) }}
          >
            {MONATSNAMEN.map((name, index) => (
              <option key={name} value={String(index + 1)}>{name}</option>
            ))}
          </select>
        </Feld>
        <Knopf disabled={bauen.isPending} onClick={starten}>Monat erstellen</Knopf>
      </Leiste>

      {bauen.isError ? (
        <>
          {/* Wörtlich die Meldung des Servers: sie nennt die fehlenden Belege namentlich. */}
          <p role="alert" className="fehler">{fehlermeldung(bauen.error)}</p>
          {belegeFehlen ? (
            <p className="knoepfe">
              <Knopf variante="still" disabled={bauen.isPending} onClick={starten}>
                Export erneut starten
              </Knopf>
            </p>
          ) : null}
        </>
      ) : null}

      {zuletzt === null ? null : (
        <p className="meldung">
          Der Export für {zeitraumText(zuletzt.period)} wurde erstellt.{' '}
          <a href={api.dokumente.url(zuletzt.id, { download: true })}>ZIP herunterladen</a>
        </p>
      )}

      <h2>Bisherige Exporte</h2>
      <Tabelle
        beschriftung="Erzeugte Steuerberater-Exporte, neueste zuerst"
        kopf={
          <tr>
            <th scope="col">Zeitraum</th>
            <th scope="col">Erstellt am</th>
            <th scope="col" className="zahl">Größe</th>
            <th scope="col">Datei</th>
          </tr>
        }
      >
        <Zeilen
          exporte={exporte.data?.exporte}
          laedt={exporte.isPending}
          fehler={exporte.isError ? exporte.error : null}
          onWiederholen={() => { void exporte.refetch() }}
        />
      </Tabelle>
    </div>
  )
}

interface ZeilenProps {
  exporte: DokumentZeile[] | undefined
  laedt: boolean
  fehler: unknown
  onWiederholen: () => void
}

function Zeilen({ exporte, laedt, fehler, onWiederholen }: ZeilenProps): ReactNode {
  if (laedt) {
    return <TabellenZustand art="laedt" beschriftung="Die Exporte werden geladen." spalten={SPALTEN} />
  }
  if (fehler !== null) {
    return <TabellenZustand art="fehler" fehler={fehler} onWiederholen={onWiederholen} spalten={SPALTEN} />
  }
  if (exporte === undefined || exporte.length === 0) {
    return (
      <TabellenZustand
        art="leer"
        titel="Noch kein Export erzeugt"
        hinweis="Wählen Sie einen Monat und erzeugen Sie den Export. Er enthält die Rechnungen, Belege und CSV-Dateien dieses Monats."
        spalten={SPALTEN}
      />
    )
  }
  return (
    <>
      {exporte.map((dokument) => (
        <tr key={dokument.id}>
          <th scope="row">{zeitraumText(dokument.period)}</th>
          <td className="mono">{datumZeit(dokument.created_at)}</td>
          <td className="zahl mono">{dateigroesse(dokument.byte_size)}</td>
          <td>
            <a href={api.dokumente.url(dokument.id, { download: true })}>ZIP herunterladen</a>
          </td>
        </tr>
      ))}
    </>
  )
}

export const Route = createFileRoute('/t/$tenantId/exporte')({ component: ExporteRoute })

function ExporteRoute(): ReactElement {
  const { tenantId } = Route.useParams()
  return <ExporteSeite tenantId={tenantId} />
}
